import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  FileText,
  MessageSquare,
  Award,
  Plus,
  Pencil,
  Trash2,
  Image,
  Link,
  Send,
  BookOpen,
  Megaphone,
  Loader2,
  LogOut,
  Video,
  Smartphone,
  Play,
  Star,
  Clock,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useLocation } from 'wouter';
import { useToast } from '@/hooks/use-toast';
import { ImageUpload } from '@/components/image-upload';
import { isImageCardArticle } from '@shared/imageArticle';

// Types
interface LearningContent {
  id: string;
  headline: string;
  subHeading?: string | null;
  bodyText?: string | null;
  imageUrl: string | null;
  headerImage?: string | null;
  thumbnailUrl?: string | null;
  linkUrl: string;
  section?: string; // 'know_the_app', 'learning', 'blogs', 'explainer'
  isFeatured?: boolean;
  isPublished: boolean;
  featureDay?: number | null;
  companyId?: string | null;
  createdAt: string;
}

interface MotivationalMessage {
  id: string;
  text: string;
  imageUrl?: string | null;
  linkUrl?: string | null;
  companyId?: string | null;
  isActive?: boolean;
  active?: boolean;
  createdAt: string;
}

interface BadgeMessage {
  id: string;
  badgeName: string;
  message: string;
}

// API functions
const fetchLearningContent = async (): Promise<LearningContent[]> => {
  const res = await apiRequest('GET', '/api/admin/content/learning');
  return res.json();
};

const fetchMotivationalMessages = async (): Promise<MotivationalMessage[]> => {
  const res = await apiRequest('GET', '/api/admin/content/messages');
  return res.json();
};

const fetchBadgeMessages = async (): Promise<BadgeMessage[]> => {
  const res = await apiRequest('GET', '/api/admin/content/badges');
  return res.json();
};

function nextFeatureDay(items: LearningContent[], companyId = ''): number {
  return (
    items
      .filter((item) => (item.companyId || '') === companyId)
      .reduce((max, item) => Math.max(max, item.featureDay || 0), 0) + 1
  );
}

function ArticleScheduleBadge({ content }: { content: LearningContent }) {
  const label = !content.isPublished
    ? 'Draft'
    : content.featureDay
      ? `Day ${content.featureDay}`
      : 'On Sauce';
  const tone = !content.isPublished
    ? 'bg-yellow-100 text-yellow-700'
    : 'bg-green-100 text-green-700';
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${tone}`}>{label}</span>
  );
}

const ContentAdminPanel = () => {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [newContent, setNewContent] = useState({
    headline: '',
    subHeading: '',
    bodyText: '',
    imageUrl: '',
    headerImage: '',
    thumbnailUrl: '',
    linkUrl: '',
    section: 'blogs',
    isFeatured: false,
    isPublished: true,
    featureDay: 1,
    companyId: '',
  });
  const [newMessage, setNewMessage] = useState('');
  const [newMessageImageUrl, setNewMessageImageUrl] = useState('');
  const [newMessageLink, setNewMessageLink] = useState('');
  const [newMessageCompanyId, setNewMessageCompanyId] = useState('');
  const [newBadge, setNewBadge] = useState({ badgeName: '', badgeEmoji: '', message: '' });
  const [editingBadge, setEditingBadge] = useState<BadgeMessage | null>(null);
  const [showContentDialog, setShowContentDialog] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [showBadgeDialog, setShowBadgeDialog] = useState(false);

  // Queries
  const { data: learningContent = [], isLoading: loadingContent } = useQuery({
    queryKey: ['content-learning'],
    queryFn: fetchLearningContent,
  });

  const { data: motivationalMessages = [], isLoading: loadingMessages } = useQuery({
    queryKey: ['content-messages'],
    queryFn: fetchMotivationalMessages,
  });

  const { data: badgeMessages = [], isLoading: loadingBadges } = useQuery({
    queryKey: ['content-badges'],
    queryFn: fetchBadgeMessages,
  });

  const { data: companies = [] } = useQuery<{ id: string; name: string; code: string }[]>({
    queryKey: ['content-companies'],
    queryFn: async () => {
      const res = await apiRequest('GET', '/api/admin/content/companies');
      return res.json();
    },
  });

  // Learning Content Mutations
  const addContentMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await apiRequest('POST', '/api/admin/content/learning', data);
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['content-learning'] });
      const live = variables?.isPublished !== false;
      const day = variables?.featureDay;
      toast({
        title: !live ? 'Draft saved' : `Article saved for day ${day || 1}`,
        description: !live
          ? 'Turn it live from the list when you are ready.'
          : variables?.companyId
            ? 'People in that company see it on that day of their journey. The next day it stays on Sauce.'
            : 'Everyone without a company code sees this sequence. The highest day is featured.',
      });
      setNewContent({
        headline: '',
        subHeading: '',
        bodyText: '',
        imageUrl: '',
        headerImage: '',
        thumbnailUrl: '',
        linkUrl: '',
        section: 'blogs',
        isFeatured: false,
        isPublished: true,
        featureDay: 1,
        companyId: '',
      });
      setShowContentDialog(false);
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to add content', variant: 'destructive' });
    },
  });

  const assignContentCompanyMutation = useMutation({
    mutationFn: async ({ id, companyId }: { id: string; companyId: string | null }) => {
      const res = await apiRequest('PUT', `/api/admin/content/learning/${id}`, { companyId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-learning'] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to change the article company',
        variant: 'destructive',
      });
    },
  });

  const togglePublishMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('PATCH', `/api/admin/content/learning/${id}/toggle-publish`);
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['content-learning'] });
      toast({
        title: data.isPublished ? 'Content published' : 'Content unpublished',
        description: `"${data.headline}" is now ${data.isPublished ? 'live' : 'a draft'}.`,
      });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update content', variant: 'destructive' });
    },
  });

  const deleteContentMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/admin/content/learning/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-learning'] });
      toast({ title: 'Content deleted', description: 'Learning content has been removed.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete content', variant: 'destructive' });
    },
  });

  // Motivational Messages Mutations
  const addMessageMutation = useMutation({
    mutationFn: async (payload: {
      text: string;
      imageUrl?: string | null;
      linkUrl?: string | null;
      companyId?: string | null;
    }) => {
      const res = await apiRequest('POST', '/api/admin/content/messages', payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-messages'] });
      toast({
        title: 'Advertisement added',
        description: 'The strip image is live for that company, or as the default.',
      });
      setNewMessage('');
      setNewMessageImageUrl('');
      setNewMessageLink('');
      setNewMessageCompanyId('');
      setShowMessageDialog(false);
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to add message', variant: 'destructive' });
    },
  });

  const assignMessageCompanyMutation = useMutation({
    mutationFn: async ({ id, companyId }: { id: string; companyId: string | null }) => {
      const res = await apiRequest('PUT', `/api/admin/content/messages/${id}`, { companyId });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-messages'] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to change the advertisement company',
        variant: 'destructive',
      });
    },
  });

  const toggleMessageMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest('PATCH', `/api/admin/content/messages/${id}/toggle`);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-messages'] });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update message', variant: 'destructive' });
    },
  });

  const deleteMessageMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/admin/content/messages/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-messages'] });
      toast({ title: 'Message deleted', description: 'Motivational message has been removed.' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete message', variant: 'destructive' });
    },
  });

  // Badge Messages Mutations
  const updateBadgeMutation = useMutation({
    mutationFn: async (data: { id: string; message: string }) => {
      const res = await apiRequest('PATCH', `/api/admin/content/badges/${data.id}`, {
        message: data.message,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-badges'] });
      toast({ title: 'Badge message updated', description: 'The badge message has been saved.' });
      setEditingBadge(null);
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update badge message',
        variant: 'destructive',
      });
    },
  });

  // Add badge mutation
  const addBadgeMutation = useMutation({
    mutationFn: async (data: { badgeName: string; badgeEmoji?: string; message: string }) => {
      const res = await apiRequest('POST', '/api/admin/content/badges', data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['content-badges'] });
      toast({ title: 'Badge added', description: 'Badge message has been added.' });
      setNewBadge({ badgeName: '', badgeEmoji: '', message: '' });
      setShowBadgeDialog(false);
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to add badge', variant: 'destructive' });
    },
  });

  const handleAddContent = () => {
    if (!newContent.headline.trim()) {
      toast({
        title: 'Headline required',
        description: 'Name the article so Sauce can label the thumbnail.',
        variant: 'destructive',
      });
      return;
    }
    if (!isImageCardArticle(newContent)) {
      toast({
        title: 'Upload two images',
        description: 'Add a thumbnail and a separate full article image.',
        variant: 'destructive',
      });
      return;
    }
    const featureDay = Math.max(1, Math.round(Number(newContent.featureDay) || 1));
    const data = {
      ...newContent,
      subHeading: newContent.subHeading || null,
      bodyText: newContent.bodyText || null,
      imageUrl: newContent.imageUrl,
      headerImage: newContent.imageUrl,
      thumbnailUrl: newContent.thumbnailUrl,
      linkUrl: newContent.linkUrl || '',
      companyId: newContent.companyId || null,
      isFeatured: false,
      featureDay,
    };
    addContentMutation.mutate(data);
  };

  const handleAddMessage = () => {
    if (newMessage.trim() || newMessageImageUrl.trim()) {
      addMessageMutation.mutate({
        text: newMessage.trim(),
        imageUrl: newMessageImageUrl.trim() || null,
        linkUrl: newMessageLink.trim() || null,
        companyId: newMessageCompanyId || null,
      });
    }
  };

  const handleUpdateBadgeMessage = () => {
    if (editingBadge) {
      updateBadgeMutation.mutate({ id: editingBadge.id, message: editingBadge.message });
    }
  };

  const handlePublishToMessaging = (content: LearningContent) => {
    toast({
      title: 'Pushed to messaging layer',
      description: `"${content.headline}" is now showing on the homepage.`,
    });
  };

  const getBadgeEmoji = (badgeName: string) => {
    switch (badgeName) {
      case 'First Drop':
        return '💧';
      case 'Week Warrior':
        return '⚡';
      case 'Hydration Hero':
        return '🏆';
      case 'Perfect Month':
        return '🌟';
      default:
        return '🏅';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Content Admin Panel</h1>
            <p className="text-muted-foreground">Manage app content and messaging</p>
          </div>
          <Button
            variant="outline"
            onClick={() => setLocation('/logout')}
            className="text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>

        <Tabs defaultValue="app-guide" className="space-y-6">
          <TabsList className="grid grid-cols-5 w-full bg-lilac-100 p-1 rounded-xl">
            <TabsTrigger value="app-guide" className="text-xs rounded-lg">
              <Smartphone className="w-3 h-3 mr-1 hidden sm:inline" />
              App Guide
            </TabsTrigger>
            <TabsTrigger value="videos" className="text-xs rounded-lg">
              <Video className="w-3 h-3 mr-1 hidden sm:inline" />
              Videos
            </TabsTrigger>
            <TabsTrigger value="learning" className="text-xs rounded-lg">
              <BookOpen className="w-3 h-3 mr-1 hidden sm:inline" />
              Blogs
            </TabsTrigger>
            <TabsTrigger value="messaging" className="text-xs rounded-lg">
              <MessageSquare className="w-3 h-3 mr-1 hidden sm:inline" />
              Ads
            </TabsTrigger>
            <TabsTrigger value="badges" className="text-xs rounded-lg">
              <Award className="w-3 h-3 mr-1 hidden sm:inline" />
              Badges
            </TabsTrigger>
          </TabsList>

          {/* App Guide Tab - Coming Soon */}
          <TabsContent value="app-guide" className="space-y-4">
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-lilac-50 border-lilac-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-foreground flex items-center gap-2">
                    <Smartphone className="w-5 h-5 text-primary" />
                    Know the App
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Feature guides, tutorials, and tips for users
                  </p>
                </div>
                <Button size="sm" className="bg-primary hover:bg-primary/90" disabled>
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
              <div className="text-center py-12 text-muted-foreground">
                <Smartphone className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">App Guide Content Coming Soon</p>
                <p className="text-sm mt-2">Backend integration required for this feature</p>
              </div>
            </Card>
          </TabsContent>

          {/* Videos Tab - Coming Soon */}
          <TabsContent value="videos" className="space-y-4">
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-lilac-50 border-lilac-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-foreground flex items-center gap-2">
                    <Video className="w-5 h-5 text-primary" />
                    Video Content
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1">
                    Micro-learning videos for users
                  </p>
                </div>
                <Button size="sm" className="bg-primary hover:bg-primary/90" disabled>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Video
                </Button>
              </div>
              <div className="text-center py-12 text-muted-foreground">
                <Video className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="font-medium">Video Content Coming Soon</p>
                <p className="text-sm mt-2">Backend integration required for this feature</p>
              </div>
            </Card>
            <Card className="p-4 bg-lilac-50 border-lilac-200">
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-500" />
                Featured videos will appear prominently in the learning section.
              </p>
            </Card>
          </TabsContent>

          {/* Learning Content Tab */}
          <TabsContent value="learning" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Learning Content
                  {loadingContent && <Loader2 className="w-4 h-4 animate-spin" />}
                </h3>
                <Dialog
                  open={showContentDialog}
                  onOpenChange={(open) => {
                    setShowContentDialog(open);
                    if (open) {
                      setNewContent((current) => ({
                        ...current,
                        featureDay: nextFeatureDay(learningContent),
                      }));
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Content
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add article</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                      <p
                        className="text-sm text-muted-foreground"
                        data-testid="image-article-format-hint"
                      >
                        Upload two images: a thumbnail for Sauce and Home, and the full article as
                        an image. The article graphic is the article — do not paste HTML.
                      </p>
                      <div className="grid gap-2">
                        <Label htmlFor="headline">Headline</Label>
                        <Input
                          id="headline"
                          value={newContent.headline}
                          onChange={(e) =>
                            setNewContent({ ...newContent, headline: e.target.value })
                          }
                          placeholder="Article title"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="subHeading">Subheading (optional)</Label>
                        <Input
                          id="subHeading"
                          value={newContent.subHeading}
                          onChange={(e) =>
                            setNewContent({ ...newContent, subHeading: e.target.value })
                          }
                          placeholder="Brief subtitle or tagline"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label>Section</Label>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          value={newContent.section}
                          onChange={(e) =>
                            setNewContent({ ...newContent, section: e.target.value })
                          }
                        >
                          <option value="blogs">Health Blog</option>
                          <option value="learning">Learning Center</option>
                          <option value="know_the_app">Know the App</option>
                          <option value="explainer">Explainer</option>
                        </select>
                      </div>

                      <div className="grid gap-2">
                        <Label>Thumbnail</Label>
                        <p className="text-xs text-muted-foreground">
                          Sauce featured card, tiles, Home ticker, and popup.
                        </p>
                        <ImageUpload
                          value={newContent.thumbnailUrl}
                          onChange={(url) => setNewContent({ ...newContent, thumbnailUrl: url })}
                          buttonLabel="thumbnail"
                          variant="card"
                          inputTestId="thumbnail-image-upload"
                          useDataUrl
                          maxEdge={1200}
                          background="#ffffff"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label>Article image</Label>
                        <p className="text-xs text-muted-foreground">
                          The full designed card shown when someone opens the article.
                        </p>
                        <ImageUpload
                          value={newContent.imageUrl}
                          onChange={(url) =>
                            setNewContent({
                              ...newContent,
                              imageUrl: url,
                              headerImage: url,
                            })
                          }
                          buttonLabel="article image"
                          variant="card"
                          inputTestId="article-image-upload"
                          useDataUrl
                          maxEdge={2048}
                          background="#ffffff"
                        />
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="linkUrl">External Link (Optional)</Label>
                        <Input
                          id="linkUrl"
                          value={newContent.linkUrl}
                          onChange={(e) =>
                            setNewContent({ ...newContent, linkUrl: e.target.value })
                          }
                          placeholder="https://..."
                        />
                      </div>

                      <div className="flex items-center gap-2 mt-2">
                        <input
                          type="checkbox"
                          id="isPublished"
                          checked={newContent.isPublished}
                          onChange={(e) =>
                            setNewContent({ ...newContent, isPublished: e.target.checked })
                          }
                          className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <Label htmlFor="isPublished" className="mb-0">
                          Publish live now
                        </Label>
                      </div>

                      <div className="grid gap-2">
                        <Label>Cohort / company</Label>
                        <p className="text-xs text-muted-foreground">
                          People in that company see this sequence. Default is everyone with no
                          company code, and that list does not follow their join date.
                        </p>
                        <select
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={newContent.companyId}
                          onChange={(e) => {
                            const companyId = e.target.value;
                            setNewContent({
                              ...newContent,
                              companyId,
                              featureDay: nextFeatureDay(learningContent, companyId),
                            });
                          }}
                        >
                          <option value="">Default (no company code)</option>
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.name} · {company.code}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="grid gap-2">
                        <Label htmlFor="featureDay">Journey day</Label>
                        <p className="text-xs text-muted-foreground">
                          {newContent.companyId
                            ? 'Day 1 is the day someone in this company joins. Day 2 is the next calendar day. After that day the article stays on Sauce.'
                            : 'This number is the order in the default list. Everyone without a company code sees the same list. The highest day is featured.'}
                        </p>
                        <Input
                          id="featureDay"
                          type="number"
                          min={1}
                          max={1000}
                          value={newContent.featureDay}
                          onChange={(e) =>
                            setNewContent({
                              ...newContent,
                              featureDay: Math.max(1, Number(e.target.value) || 1),
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <Button variant="outline" onClick={() => setShowContentDialog(false)}>
                        Cancel
                      </Button>
                      <Button
                        className="bg-purple-600 hover:bg-purple-700"
                        onClick={handleAddContent}
                        disabled={
                          addContentMutation.isPending ||
                          !newContent.headline.trim() ||
                          !isImageCardArticle(newContent)
                        }
                      >
                        {addContentMutation.isPending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Adding...
                          </>
                        ) : !newContent.isPublished ? (
                          'Save draft'
                        ) : (
                          'Publish article'
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <ScrollArea className="h-80">
                <div className="space-y-3">
                  {learningContent.map((content) => (
                    <div
                      key={content.id}
                      className="flex items-center gap-4 p-4 bg-lilac-50 rounded-xl"
                    >
                      <div className="w-16 h-16 bg-lilac-200 rounded-lg flex items-center justify-center overflow-hidden">
                        {content.thumbnailUrl || content.imageUrl ? (
                          <img
                            src={content.thumbnailUrl || content.imageUrl || ''}
                            alt=""
                            className="w-full h-full object-contain bg-[#7ee7f2]"
                          />
                        ) : (
                          <Image className="w-8 h-8 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-foreground">{content.headline}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-1">
                          <Link className="w-3 h-3" />
                          {content.linkUrl}
                        </div>
                        <ArticleScheduleBadge content={content} />
                        <select
                          aria-label={`Company for ${content.headline}`}
                          className="mt-1 flex h-8 max-w-full rounded-md border border-input bg-background px-2 text-xs"
                          value={content.companyId || ''}
                          onChange={(e) =>
                            assignContentCompanyMutation.mutate({
                              id: content.id,
                              companyId: e.target.value || null,
                            })
                          }
                        >
                          <option value="">Default (no company code)</option>
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.name} · {company.code}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePublishToMessaging(content)}
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => togglePublishMutation.mutate(content.id)}
                          className="text-muted-foreground hover:text-foreground"
                          disabled={togglePublishMutation.isPending}
                          aria-label={content.isPublished ? 'Unpublish article' : 'Publish article'}
                        >
                          {togglePublishMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : content.isPublished ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteContentMutation.mutate(content.id)}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          disabled={deleteContentMutation.isPending}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {learningContent.length === 0 && !loadingContent && (
                    <div className="text-center text-muted-foreground py-8">
                      No learning content yet
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>

            {/* Push to Homepage Card */}
            <Card className="p-6 bg-gradient-to-br from-purple-50 to-lilac-100">
              <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-purple-600" />
                Push to Homepage Messaging Layer
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Select content above and click the send icon to push it to the homepage messaging
                layer.
              </p>
            </Card>
          </TabsContent>

          {/* Messaging Layer Tab */}
          <TabsContent value="messaging" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Advertisements
                  {loadingMessages && <Loader2 className="w-4 h-4 animate-spin" />}
                </h3>
                <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-purple-500 hover:bg-purple-600">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Advertisement
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-background max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Add advertisement</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>Strip image</Label>
                        <div className="mt-1">
                          <ImageUpload
                            value={newMessageImageUrl}
                            onChange={(url) => setNewMessageImageUrl(url)}
                            useDataUrl
                            maxEdge={1600}
                            background="#ffffff"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          This image fills the whole home strip. A wide image works best, about
                          1200×400.
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="adLink">Link (optional)</Label>
                        <Input
                          id="adLink"
                          value={newMessageLink}
                          onChange={(e) => setNewMessageLink(e.target.value)}
                          placeholder="https://… or /analytics"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Tapping the strip opens this page. Leave it empty for an image with no
                          link.
                        </p>
                      </div>
                      <div>
                        <Label>Cohort / company</Label>
                        <select
                          className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={newMessageCompanyId}
                          onChange={(e) => setNewMessageCompanyId(e.target.value)}
                        >
                          <option value="">Default (no company code)</option>
                          {companies.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.name} · {company.code}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label>Alt text (optional)</Label>
                        <Textarea
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Shown only if there is no image"
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                      <Button
                        onClick={handleAddMessage}
                        className="w-full bg-purple-500 hover:bg-purple-600"
                        disabled={
                          addMessageMutation.isPending ||
                          (!newMessage.trim() && !newMessageImageUrl.trim())
                        }
                      >
                        {addMessageMutation.isPending && (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        )}
                        Add Advertisement
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <ScrollArea className="h-80">
                <div className="space-y-3">
                  {motivationalMessages.map((msg) => {
                    const isActive = msg.isActive ?? msg.active ?? false;
                    return (
                      <div
                        key={msg.id}
                        className={`flex items-center justify-between p-4 rounded-xl transition-all gap-3 overflow-hidden ${
                          isActive
                            ? 'bg-lilac-100 border border-lilac-300'
                            : 'bg-muted/30 opacity-60'
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          {msg.imageUrl && (
                            <img
                              src={msg.imageUrl}
                              alt=""
                              className="w-full max-h-40 object-contain rounded-lg mb-2 bg-white"
                            />
                          )}
                          {msg.text ? (
                            <p className="text-sm text-foreground break-words">{msg.text}</p>
                          ) : (
                            <p className="text-xs text-muted-foreground italic">Image strip</p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1 break-all">
                            {msg.linkUrl ? msg.linkUrl : 'No link'}
                          </p>
                          <select
                            aria-label="Advertisement company"
                            className="mt-1 flex h-8 max-w-full rounded-md border border-input bg-background px-2 text-xs"
                            value={msg.companyId || ''}
                            onChange={(e) =>
                              assignMessageCompanyMutation.mutate({
                                id: msg.id,
                                companyId: e.target.value || null,
                              })
                            }
                          >
                            <option value="">Default (no company code)</option>
                            {companies.map((company) => (
                              <option key={company.id} value={company.id}>
                                {company.name} · {company.code}
                              </option>
                            ))}
                          </select>
                          <span
                            className={`text-xs ${isActive ? 'text-green-600' : 'text-muted-foreground'}`}
                          >
                            {isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleMessageMutation.mutate(msg.id)}
                            className="text-muted-foreground hover:text-foreground"
                            disabled={toggleMessageMutation.isPending}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteMessageMutation.mutate(msg.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            disabled={deleteMessageMutation.isPending}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                  {motivationalMessages.length === 0 && !loadingMessages && (
                    <div className="text-center text-muted-foreground py-8">
                      No advertisements yet
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>

            <Card className="p-4 bg-lilac-50">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> The active image for a company fills that company's home
                strip. People with no company code see the default advertisement.
              </p>
            </Card>
          </TabsContent>

          {/* Badge Messages Tab */}
          <TabsContent value="badges" className="space-y-4">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground flex items-center gap-2">
                  <Award className="w-5 h-5 text-yellow-500" />
                  Badge Messages
                  {loadingBadges && <Loader2 className="w-4 h-4 animate-spin" />}
                </h3>
                <Dialog open={showBadgeDialog} onOpenChange={setShowBadgeDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="bg-purple-500 hover:bg-purple-600">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Badge
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-background">
                    <DialogHeader>
                      <DialogTitle>Add Badge Message</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 pt-4">
                      <div>
                        <Label>Badge Name</Label>
                        <Input
                          value={newBadge.badgeName}
                          onChange={(e) => setNewBadge({ ...newBadge, badgeName: e.target.value })}
                          placeholder="e.g., First Drop, Week Warrior"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Badge Emoji</Label>
                        <Input
                          value={newBadge.badgeEmoji}
                          onChange={(e) => setNewBadge({ ...newBadge, badgeEmoji: e.target.value })}
                          placeholder="💧"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Badge Message</Label>
                        <Textarea
                          value={newBadge.message}
                          onChange={(e) => setNewBadge({ ...newBadge, message: e.target.value })}
                          placeholder="Congratulations message..."
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                      <Button
                        onClick={() => {
                          if (newBadge.badgeName && newBadge.message) {
                            addBadgeMutation.mutate(newBadge);
                          }
                        }}
                        className="w-full bg-purple-500 hover:bg-purple-600"
                        disabled={addBadgeMutation.isPending}
                      >
                        {addBadgeMutation.isPending && (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        )}
                        Add Badge
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <ScrollArea className="h-80">
                <div className="space-y-3">
                  {badgeMessages.map((badge) => (
                    <div
                      key={badge.id}
                      className="p-4 bg-gradient-to-r from-yellow-50 to-lilac-50 rounded-xl"
                    >
                      {editingBadge?.id === badge.id ? (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">{getBadgeEmoji(badge.badgeName)}</span>
                            <span className="font-medium">{badge.badgeName}</span>
                          </div>
                          <Textarea
                            value={editingBadge.message}
                            onChange={(e) =>
                              setEditingBadge({ ...editingBadge, message: e.target.value })
                            }
                            rows={2}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={handleUpdateBadgeMessage}
                              className="bg-purple-500 hover:bg-purple-600"
                              disabled={updateBadgeMutation.isPending}
                            >
                              {updateBadgeMutation.isPending && (
                                <Loader2 className="w-4 h-4 animate-spin mr-1" />
                              )}
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingBadge(null)}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <span className="text-2xl">{getBadgeEmoji(badge.badgeName)}</span>
                            <div>
                              <div className="font-medium text-foreground">{badge.badgeName}</div>
                              <p className="text-sm text-muted-foreground">{badge.message}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingBadge(badge)}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {badgeMessages.length === 0 && !loadingBadges && (
                    <div className="text-center text-muted-foreground py-8">
                      No badge messages configured
                    </div>
                  )}
                </div>
              </ScrollArea>
            </Card>

            <Card className="p-4 bg-lilac-50">
              <p className="text-sm text-muted-foreground">
                <strong>Note:</strong> These messages are shown to users when they earn the
                corresponding badge.
              </p>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ContentAdminPanel;
